import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { Link } from 'site-modules/shared/components/link/link';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';

import './content-block.scss';

/* eslint-disable react/no-array-index-key */

function renderItems(items, { sectionTitleClassName, sectionSubtitleClassName, paragraphTitleClassName }) {
  return items.map((item, ind) => {
    const { title: sectionTitle, subtitle, paragraphTitle, text, withoutMargin, list, hasHTMLContent } = item;

    if (sectionTitle) {
      return (
        <h3 key={ind} className={classnames(sectionTitleClassName, { 'mb-0': withoutMargin })}>
          {sectionTitle}
        </h3>
      );
    }

    if (subtitle) {
      return (
        <h4 key={ind} className={classnames(sectionSubtitleClassName, { 'mb-0': withoutMargin })}>
          {subtitle}
        </h4>
      );
    }

    if (paragraphTitle) {
      return (
        <h5 key={ind} className={classnames(paragraphTitleClassName, { 'mb-0': withoutMargin })}>
          {paragraphTitle}
        </h5>
      );
    }

    if (list) {
      return (
        <ul className="body" key={ind}>
          {list.map((listItem, index) =>
            hasHTMLContent ? (
              <ContentFragment key={`list-${index}`} componentToUse="li">
                {listItem}
              </ContentFragment>
            ) : (
              <li key={`list-${index}`}>{listItem}</li>
            )
          )}
        </ul>
      );
    }

    if (hasHTMLContent) {
      return (
        <ContentFragment key={ind} componentToUse="p">
          {text}
        </ContentFragment>
      );
    }

    return <p key={ind}>{text}</p>;
  });
}

export function ContentBlock({
  title = null,
  items,
  author = null,
  itemsBeforeCollapseNum = null,
  className = 'content-block-border p-1 mb-1_5',
  titleClassName = 'heading-4 mb-0',
  sectionTitleClassName = 'heading-5',
  sectionSubtitleClassName = '',
  paragraphTitleClassName = '',
}) {
  const itemsBeforeCollapse = itemsBeforeCollapseNum ? items.slice(0, itemsBeforeCollapseNum) : items;
  const itemsInsideCollapse = itemsBeforeCollapseNum ? items.slice(itemsBeforeCollapseNum) : [];
  const blocksClasses = { sectionTitleClassName, sectionSubtitleClassName, paragraphTitleClassName };

  return (
    <section className={classnames('content-block', className)}>
      {title && <h2 className={titleClassName}>{title}</h2>}
      {author && (
        <p className="small text-gray-darker mb-0_5">
          <Link to={author.link}>{author.title}</Link>
        </p>
      )}
      {renderItems(itemsBeforeCollapse, blocksClasses)}
      {Boolean(itemsInsideCollapse.length) && (
        <ContentCollapse
          textClosed="Read more"
          textOpen="Read less"
          justify="justify-content-start"
          btnContainerClasses="m-0 p-0"
          btnClasses="p-0 size-16 text-primary-darker more-info-btn"
          btnTextClasses="fw-normal"
          showBtnBelow
          isEclipseFade={false}
        >
          {renderItems(itemsInsideCollapse, blocksClasses)}
        </ContentCollapse>
      )}
    </section>
  );
}

ContentBlock.propTypes = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      title: PropTypes.string,
      withoutMargin: PropTypes.bool,
      list: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  author: PropTypes.shape({
    title: PropTypes.string,
    link: PropTypes.string,
  }),
  itemsBeforeCollapseNum: PropTypes.number,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
  sectionTitleClassName: PropTypes.string,
  sectionSubtitleClassName: PropTypes.string,
  paragraphTitleClassName: PropTypes.string,
};
